import React, { useEffect, useRef, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { getReachEstimateStyles } from './reach-estimate-styles';


const meterProps = {
  value: 0,
  max: 100,
  min: 0,
};

const calcBroadMeterValue = (estimatedImpressions) => {
  // Use logarithmic scale to represent impressions: y = a ln x + b
  // 96 = a ln 466B + b: (466B experimentally derived as max impressions value)
  // 20 = a ln 1M + b: (1M is required as min broad impressions value)
  // TODO: Tune this as needed after initial self-hosting
  const transformedVal = (5.82288 * Math.log(estimatedImpressions)) - 60.44618;

  return transformedVal;
};

export const AudienceMeter = (props) => {
  const {
    estimatedImpressions, isImpressionTooSpecific, i18n, isDealEstimate,
  } = props;

  const classes = useDefaultStyles(getReachEstimateStyles);

  const arrowRef = useRef(null);
  const specificMeterRef = useRef(null);
  const broadMeterRef = useRef(null);

  const [arrowStyles, setArrowStyles] = useState(null);
  const [arrowType, setArrowType] = useState(null);
  const [broadMeterStyle, setBroadMeterStyle] = useState(null);
  const [specificMeterStyle, setSpecificMeterStyle] = useState(null);

  const calcBroadPos = useCallback((value) => {
    const specificMeterSize = specificMeterRef.current.offsetWidth;
    const broadMeterSize = broadMeterRef.current.offsetWidth;
    const arrowGap = arrowRef.current.style.borderLeftWidth;

    const broadScalePos = value * ((broadMeterSize - arrowGap) / meterProps.max);

    // Offset final position with specific meter width
    return (0.1 * specificMeterSize) + broadScalePos;
  }, [arrowRef, specificMeterRef, broadMeterRef]);

  const moveAlongBroad = useCallback((value) => {
    if (arrowRef && arrowRef.current) {
      setArrowStyles({
        left: calcBroadPos(value),
        borderTopColor: 'green',
        backgroundColor: '#107C10',
      });
      setArrowType('arrow-broad');
    }

    if (isDealEstimate && specificMeterRef.current && broadMeterRef.current) {
      const totalWidth = (0.1 * specificMeterRef.current.clientWidth) + broadMeterRef.current.clientWidth;
      const estimatePos = calcBroadPos(value);

      setSpecificMeterStyle({
        width: '100%',
      });
      setBroadMeterStyle({
        width: `${(estimatePos / totalWidth) * 100}%`,
        marginLeft: 0,
      });
      setArrowStyles({
        visibility: 'hidden',
      });
    }
  }, [isDealEstimate, calcBroadPos]);

  const calcSpecificPos = useCallback((value) => {
    const specificMeterSize = specificMeterRef.current.offsetWidth;
    const arrowGap = arrowRef.current.style.borderLeftWidth;

    return value * ((specificMeterSize - arrowGap) / meterProps.max);
  }, [arrowRef, specificMeterRef]);

  const moveToSpecific = useCallback(() => {
    const midPointVal = 50; // percentage

    if (arrowRef && arrowRef.current) {
      setArrowStyles({
        left: calcSpecificPos(midPointVal),
        borderTopColor: 'red',
        backgroundColor: '#A80000',
      });
      setArrowType('arrow-narrow');
    }
  }, [calcSpecificPos]);

  useEffect(() => {
    if (arrowRef.current && specificMeterRef.current && broadMeterRef.current) {
      if (isImpressionTooSpecific) {
        moveToSpecific();
      } else {
        moveAlongBroad(calcBroadMeterValue(estimatedImpressions));
      }
    }
  }, [moveAlongBroad, moveToSpecific, estimatedImpressions, isImpressionTooSpecific, arrowRef, specificMeterRef, broadMeterRef]);

  return (
    <div className={classes.meterContainer}>
      <div ref={arrowRef} className={classNames(classes.arrow, arrowType)} style={arrowStyles} />
      <div className={classes.meter}>
        <div ref={specificMeterRef} style={specificMeterStyle} className="specificMeter" />
        <div ref={broadMeterRef} style={broadMeterStyle} className="broadMeter" />
      </div>
      <div className={classes.audienceSpecificLabel}>
        {i18n.getString(_TL_('Low'))}
      </div>
      <div className={classes.audienceBroadLabel}>
        {i18n.getString(_TL_('High'))}
      </div>
    </div>
  );
};

AudienceMeter.propTypes = {
  estimatedImpressions: PropTypes.number,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  isImpressionTooSpecific: PropTypes.bool,
  isDealEstimate: PropTypes.bool,
};

AudienceMeter.defaultProps = {
  estimatedImpressions: null,
  isImpressionTooSpecific: false,
  isDealEstimate: false,
};
